// OzPol Transparent — Australian Political Funding Explorer
import React, { useState, useEffect } from "react";
import { useTheme } from "../context/ThemeContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line, Legend, ResponsiveContainer,
} from "recharts";
import {
  Search, Loader2, Zap, DollarSign, Trophy,
  ChevronDown, ChevronUp, BarChart2, TrendingUp, ExternalLink,
} from "lucide-react";
import PartyDetailView from "../components/donations/PartyDetailView";
import DonorProfileView from "../components/donations/DonorProfileView";
import { Stat } from "../components/donations/DonationsHelpers";

const fmt = (n) => n >= 1e6
  ? `$${(n / 1e6).toFixed(1)}M`
  : n >= 1e3
    ? `$${(n / 1e3).toFixed(0)}K`
    : `$${n?.toLocaleString() ?? 0}`;

// ── Funding Timeline Data (AEC declared income, FY 2020–21 → 2023–24) ──
// Source: AEC Transparency Register. Figures in AUD millions.
const TIMELINE_DATA = [
  { year: "2020–21", ALP: 91.2, Liberal: 78.4, Nationals: 12.1, Greens: 14.8, ONP: 3.2, UAP: 1.8 },
  { year: "2021–22", ALP: 74.3, Liberal: 53.1, Nationals: 9.8,  Greens: 11.9, ONP: 2.8, UAP: 7.4 },
  { year: "2022–23", ALP: 104.7,Liberal: 75.8, Nationals: 11.4, Greens: 20.3, ONP: 2.4, UAP: 2.1 },
  { year: "2023–24", ALP: 88.6, Liberal: 71.2, Nationals: 10.9, Greens: 17.8, ONP: 2.9, UAP: 1.4 },
];

const PARTY_LINES = [
  { key: "ALP",       color: "#E11D48", name: "ALP (Labor)" },
  { key: "Liberal",   color: "#2563EB", name: "Liberal Party" },
  { key: "Greens",    color: "#10B981", name: "Australian Greens" },
  { key: "Nationals", color: "#065F46", name: "The Nationals" },
  { key: "UAP",       color: "#D97706", name: "UAP" },
  { key: "ONP",       color: "#92400E", name: "One Nation" },
];

const API = process.env.REACT_APP_BACKEND_URL;
const YEARS = ["2022-23", "2023-24"];
const CATEGORIES = [
  "All", "Union", "Mining", "Property", "Finance",
  "Gambling", "Political Entity", "Individual", "Other",
];
export default function DonationsPage() {
  const { isDark } = useTheme();
  const bg      = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text    = isDark ? "#F8FAFC" : "#0F172A";
  const muted   = isDark ? "#94A3B8" : "#6B7280";
  const bdr     = isDark ? "#334155" : "#000000";

  const [tab, setTab] = useState("party");

  // Party Deep Dive
  const [partyYear, setPartyYear] = useState("2022-23");
  const [parties, setParties] = useState([]);
  const [selectedPartyId, setSelectedPartyId] = useState(null);
  const [partyDetail, setPartyDetail] = useState(null);
  const [partyLoading, setPartyLoading] = useState(false);
  const [explainStates, setExplainStates] = useState({});

  // Follow The Money
  const [donors, setDonors] = useState([]);
  const [donorSearch, setDonorSearch] = useState("");
  const [donorCategory, setDonorCategory] = useState("All");
  const [selectedDonorId, setSelectedDonorId] = useState(null);
  const [donorProfile, setDonorProfile] = useState(null);
  const [donorLoading, setDonorLoading] = useState(false);

  // Leaderboard
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderYear, setLeaderYear] = useState("");
  const [leaderLoading, setLeaderLoading] = useState(false);

  // ── Effects ────────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/api/donations/parties?year=${partyYear}`)
      .then(r => r.json()).then(setParties).catch(console.error);
  }, [partyYear]);

  useEffect(() => {
    if (!selectedPartyId) return;
    setPartyLoading(true);
    setPartyDetail(null);
    setExplainStates({});
    fetch(`${API}/api/donations/party/${selectedPartyId}?year=${partyYear}`)
      .then(r => r.json())
      .then(d => { setPartyDetail(d); setPartyLoading(false); })
      .catch(() => setPartyLoading(false));
  }, [selectedPartyId, partyYear]);

  useEffect(() => {
    if (tab !== "donor") return;
    const t = setTimeout(() => {
      const p = new URLSearchParams();
      if (donorCategory !== "All") p.set("category", donorCategory);
      if (donorSearch.trim()) p.set("search", donorSearch.trim());
      fetch(`${API}/api/donations/donors?${p}`)
        .then(r => r.json()).then(setDonors).catch(console.error);
    }, 300);
    return () => clearTimeout(t);
  }, [tab, donorSearch, donorCategory]);

  useEffect(() => {
    if (!selectedDonorId) return;
    setDonorLoading(true);
    setDonorProfile(null);
    fetch(`${API}/api/donations/donor/${selectedDonorId}`)
      .then(r => r.json())
      .then(d => { setDonorProfile(d); setDonorLoading(false); })
      .catch(() => setDonorLoading(false));
  }, [selectedDonorId]);

  useEffect(() => {
    if (tab !== "leaderboard") return;
    setLeaderLoading(true);
    const p = leaderYear ? `?year=${leaderYear}&limit=20` : "?limit=20";
    fetch(`${API}/api/donations/leaderboard${p}`)
      .then(r => r.json())
      .then(d => { setLeaderboard(d); setLeaderLoading(false); })
      .catch(() => setLeaderLoading(false));
  }, [tab, leaderYear]);

  // ── Handlers ───────────────────────────────────────────────────────
  const handleExplain = async (donorId, partyId, year, amount) => {
    const key = `${donorId}::${partyId}::${year}`;
    const cur = explainStates[key];
    if (cur?.text) {
      setExplainStates(prev => ({ ...prev, [key]: { ...cur, expanded: !cur.expanded } }));
      return;
    }
    setExplainStates(prev => ({ ...prev, [key]: { loading: true, text: null, expanded: true } }));
    try {
      const res = await fetch(`${API}/api/donations/explain`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ donor_id: donorId, party_id: partyId, financial_year: year, amount_aud: amount }),
      });
      const data = await res.json();
      setExplainStates(prev => ({ ...prev, [key]: { loading: false, text: data.explanation, expanded: true } }));
    } catch {
      setExplainStates(prev => ({
        ...prev, [key]: { loading: false, text: "AI explanation failed. Please try again.", expanded: true }
      }));
    }
  };

  // ── Tab: Party Deep Dive ───────────────────────────────────────────
  const renderPartyTab = () => (
    <div data-testid="party-tab-content">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: muted, letterSpacing: "0.05em" }}>FINANCIAL YEAR:</span>
        {YEARS.map(y => (
          <button key={y} data-testid={`year-btn-${y}`} onClick={() => setPartyYear(y)} style={{
            padding: "5px 14px", fontWeight: 700, fontSize: 12, fontFamily: "Outfit, sans-serif",
            border: "2px solid #000", cursor: "pointer",
            background: partyYear === y ? "#FFC82E" : surface,
            color: partyYear === y ? "#000" : text,
            boxShadow: partyYear === y ? "2px 2px 0 #000" : "none",
          }}>{y}</button>
        ))}
      </div>

      {/* Party cards */}
      <div style={{
        display: "grid", gap: 12, marginBottom: 24,
        gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
      }}>
        {parties.map(p => (
          <button key={p.id} data-testid={`party-card-${p.id}`}
            onClick={() => setSelectedPartyId(p.id === selectedPartyId ? null : p.id)}
            style={{
              padding: 16, border: "2px solid",
              borderColor: selectedPartyId === p.id ? "#000" : bdr,
              background: selectedPartyId === p.id ? p.color : surface,
              cursor: "pointer", textAlign: "left",
              boxShadow: selectedPartyId === p.id ? "4px 4px 0 #000" : "none",
              transition: "all 0.15s",
            }}>
            <div style={{
              fontSize: 22, fontWeight: 900, fontFamily: "Outfit, sans-serif",
              color: selectedPartyId === p.id ? "#fff" : p.color, marginBottom: 3,
            }}>{p.abbreviation}</div>
            <div style={{ fontSize: 10, color: selectedPartyId === p.id ? "rgba(255,255,255,0.8)" : muted, marginBottom: 8 }}>
              {p.name}
            </div>
            <div style={{
              fontSize: 17, fontWeight: 800, fontFamily: "Outfit, sans-serif",
              color: selectedPartyId === p.id ? "#FFC82E" : text,
            }}>{fmt(p.total_declared_aud)}</div>
            <div style={{ fontSize: 9, color: selectedPartyId === p.id ? "rgba(255,255,255,0.65)" : muted }}>
              Declared Income {partyYear}
            </div>
            {p.top3_concentration_pct > 0 && (
              <div style={{ marginTop: 8, fontSize: 9, fontWeight: 700, color: selectedPartyId === p.id ? "#FFC82E" : "#F59E0B" }}>
                Top 3 donors: {p.top3_concentration_pct}% of total
              </div>
            )}
          </button>
        ))}
      </div>

      {selectedPartyId && (
        partyLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <Loader2 className="animate-spin" style={{ color: "#FFC82E", width: 32, height: 32 }} />
          </div>
        ) : partyDetail && (
          <PartyDetailView
            detail={partyDetail}
            year={partyYear}
            isDark={isDark}
            explainStates={explainStates}
            onExplain={handleExplain}
          />
        )
      )}

      {!selectedPartyId && (
        <div style={{
          border: "2px dashed", borderColor: isDark ? "#334155" : "#D1D5DB",
          padding: 32, textAlign: "center",
          background: isDark ? "#0F172A" : "#FAFAFA",
        }}>
          <BarChart2 style={{ width: 28, height: 28, color: muted, margin: "0 auto 8px" }} />
          <p style={{ color: muted, fontSize: 14, fontWeight: 600, margin: 0 }}>
            Select a party above to explore its funding sources and donor breakdown
          </p>
        </div>
      )}
    </div>
  );

  // ── Tab: Follow The Money ──────────────────────────────────────────
  const renderDonorTab = () => (
    <div data-testid="donor-tab-content">
      {selectedDonorId && donorProfile ? (
        donorLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <Loader2 className="animate-spin" style={{ color: "#FFC82E", width: 32, height: 32 }} />
          </div>
        ) : (
          <DonorProfileView
            profile={donorProfile}
            isDark={isDark}
            onBack={() => { setSelectedDonorId(null); setDonorProfile(null); }}
          />
        )
      ) : (
        <>
          {/* Search + filters */}
          <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
              <Search style={{
                position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                width: 14, height: 14, color: muted, pointerEvents: "none",
              }} />
              <input
                data-testid="donor-search-input"
                value={donorSearch}
                onChange={e => setDonorSearch(e.target.value)}
                placeholder="Search donors..."
                style={{
                  width: "100%", padding: "8px 12px 8px 32px",
                  border: "2px solid #000", background: surface, color: text,
                  fontSize: 13, fontFamily: "Outfit, sans-serif", outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setDonorCategory(c)} style={{
                padding: "4px 12px", fontSize: 10, fontWeight: 700,
                border: "2px solid #000", cursor: "pointer",
                background: donorCategory === c ? "#FFC82E" : surface,
                color: donorCategory === c ? "#000" : text,
                fontFamily: "Outfit, sans-serif",
              }}>{c}</button>
            ))}
          </div>

          {/* Donor grid */}
          <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
            {donors.map(d => (
              <button
                key={d.id}
                data-testid={`donor-card-${d.id}`}
                onClick={() => setSelectedDonorId(d.id)}
                style={{
                  padding: 14, border: "2px solid #000", background: surface,
                  cursor: "pointer", textAlign: "left", boxShadow: "2px 2px 0 #000",
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translate(-1px,-1px)"; e.currentTarget.style.boxShadow = "3px 3px 0 #000"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "2px 2px 0 #000"; }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div style={{ fontWeight: 800, fontSize: 14, color: text, lineHeight: 1.3, flex: 1 }}>
                    {d.name}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 900, fontFamily: "Outfit, sans-serif", color: "#FFC82E", marginLeft: 8 }}>
                    {fmt(d.total_declared_aud)}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 7px", border: "2px solid #000", background: "#0F172A", color: "#fff" }}>
                    {d.category}
                  </span>
                  <span style={{ fontSize: 9, fontWeight: 600, padding: "1px 7px", border: `1px solid ${bdr}`, color: muted }}>
                    {d.industry}
                  </span>
                  {d.cross_party && (
                    <span style={{ fontSize: 9, fontWeight: 800, padding: "1px 7px", border: "2px solid #EF4444", color: "#EF4444", background: "#FEE2E2" }}>
                      CROSS-PARTY
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 10, color: muted }}>
                  Funds {d.num_parties_funded} {d.num_parties_funded === 1 ? "party" : "parties"}
                  {d.parent_entity ? ` · Part of ${d.parent_entity}` : ""}
                </div>
                {/* Link row */}
                <div style={{ display: "flex", gap: 8, marginTop: 8, borderTop: `1px solid ${isDark ? "#334155" : "#E5E7EB"}`, paddingTop: 8 }}>
                  {d.website && (
                    <a href={d.website} target="_blank" rel="noopener noreferrer"
                      data-testid={`donor-card-website-${d.id}`}
                      onClick={e => e.stopPropagation()}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        fontSize: 9, fontWeight: 800, color: "#FFC82E",
                        textDecoration: "none", padding: "2px 0",
                      }}>
                      <ExternalLink style={{ width: 9, height: 9 }} /> Website
                    </a>
                  )}
                  <a href="https://transparency.aec.gov.au/" target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      fontSize: 9, fontWeight: 800, color: muted,
                      textDecoration: "none", padding: "2px 0",
                    }}>
                    <ExternalLink style={{ width: 9, height: 9 }} /> AEC Register
                  </a>
                </div>
              </button>
            ))}
            {donors.length === 0 && (
              <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: 32, color: muted }}>
                No donors found. Try adjusting the filters.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  // ── Tab: Leaderboard ──────────────────────────────────────────────
  const renderLeaderboard = () => {
    const maxAmt = Math.max(...leaderboard.map(d => d.total_aud), 1);
    return (
      <div data-testid="leaderboard-tab-content">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: muted, letterSpacing: "0.05em" }}>FILTER BY YEAR:</span>
          {[{ label: "All Years", value: "" }, ...YEARS.map(y => ({ label: y, value: y }))].map(({ label, value }) => (
            <button key={value} data-testid={`leader-year-${label.replace(" ", "-")}`}
              onClick={() => setLeaderYear(value)} style={{
                padding: "5px 14px", fontWeight: 700, fontSize: 12,
                border: "2px solid #000", cursor: "pointer", fontFamily: "Outfit, sans-serif",
                background: leaderYear === value ? "#FFC82E" : surface,
                color: leaderYear === value ? "#000" : text,
                boxShadow: leaderYear === value ? "2px 2px 0 #000" : "none",
              }}>{label}</button>
          ))}
        </div>

        {leaderLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <Loader2 className="animate-spin" style={{ color: "#FFC82E", width: 32, height: 32 }} />
          </div>
        ) : (
          <div style={{ border: "2px solid #000", overflow: "hidden", boxShadow: "4px 4px 0 #000" }}>
            <div style={{ padding: "12px 16px", background: "#0F172A", color: "#fff", display: "flex", alignItems: "center", gap: 8 }}>
              <Trophy style={{ width: 16, height: 16, color: "#FFC82E" }} />
              <span style={{ fontWeight: 800, fontSize: 13, fontFamily: "Outfit, sans-serif" }}>
                TOP POLITICAL DONORS — {leaderYear || "ALL YEARS"}
              </span>
            </div>
            {leaderboard.map((d, i) => (
              <div key={d.donor_id} data-testid={`leaderboard-row-${d.donor_id}`}
                style={{
                  padding: "14px 16px",
                  background: i % 2 === 0 ? surface : (isDark ? "#0F172A" : "#F9F7F2"),
                  borderBottom: `1px solid ${isDark ? "#334155" : "#E5E7EB"}`,
                }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  {/* Rank */}
                  <div style={{
                    width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
                    border: "2px solid #000", fontWeight: 900, fontSize: 14,
                    fontFamily: "Outfit, sans-serif",
                    background: i < 3 ? "#FFC82E" : surface, color: "#000",
                    flexShrink: 0,
                  }}>{i + 1}</div>

                  {/* Name + badges */}
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: text, marginBottom: 3 }}>{d.donor_name}</div>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 7px", border: "2px solid #000", background: "#0F172A", color: "#fff" }}>
                        {d.category}
                      </span>
                      {d.cross_party && (
                        <span style={{ fontSize: 9, fontWeight: 800, padding: "1px 7px", border: "2px solid #EF4444", color: "#EF4444", background: "#FEE2E2" }}>
                          CROSS-PARTY
                        </span>
                      )}
                      {d.parent_entity && (
                        <span style={{ fontSize: 9, color: muted, padding: "1px 7px", border: `1px solid ${bdr}` }}>
                          {d.parent_entity}
                        </span>
                      )}
                      {d.website && (
                        <a href={d.website} target="_blank" rel="noopener noreferrer"
                          data-testid={`leaderboard-website-${d.donor_id}`}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 3,
                            fontSize: 9, fontWeight: 800, color: "#FFC82E",
                            textDecoration: "none",
                          }}>
                          <ExternalLink style={{ width: 8, height: 8 }} /> Website
                        </a>
                      )}
                      <a href="https://transparency.aec.gov.au/" target="_blank" rel="noopener noreferrer"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 3,
                          fontSize: 9, fontWeight: 800, color: muted,
                          textDecoration: "none",
                        }}>
                        <ExternalLink style={{ width: 8, height: 8 }} /> AEC
                      </a>
                    </div>
                  </div>

                  {/* Primary party */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 120 }}>
                    <div style={{ width: 10, height: 10, background: d.primary_party_color, border: "1px solid #000" }} />
                    <span style={{ fontSize: 11, color: text, fontWeight: 600 }}>{d.primary_party_name}</span>
                  </div>

                  {/* Amount */}
                  <div style={{ fontSize: 18, fontWeight: 900, fontFamily: "Outfit, sans-serif", color: "#FFC82E", minWidth: 80, textAlign: "right" }}>
                    {fmt(d.total_aud)}
                  </div>
                </div>

                {/* Bar */}
                <div style={{ marginTop: 8, marginLeft: 44 }}>
                  <div style={{ background: isDark ? "#0F172A" : "#E5E7EB", height: 8, border: "1px solid #000", maxWidth: 600 }}>
                    <div style={{
                      width: `${(d.total_aud / maxAmt) * 100}%`, height: "100%",
                      background: d.color, transition: "width 0.6s ease",
                    }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 16, padding: "10px 0" }}>
          <p style={{ fontSize: 10, color: muted, margin: 0 }}>
            Source: AEC Transparency Register. Aggregated across selected financial year(s). Figures represent declared income only.{" "}
            <a href="https://transparency.aec.gov.au" target="_blank" rel="noopener noreferrer"
              style={{ color: "#FFC82E", fontWeight: 700 }}>
              View raw AEC data
            </a>
          </p>
        </div>
      </div>
    );
  };

  const renderTimeline = () => {
    const surface2 = isDark ? "#1E293B" : "#FFFFFF";
    const bdr2 = isDark ? "rgba(51,65,85,0.5)" : "rgba(226,232,240,0.85)";

    // Delta helpers
    const first = TIMELINE_DATA[0];
    const last  = TIMELINE_DATA[TIMELINE_DATA.length - 1];
    const delta = (key) => (((last[key] - first[key]) / first[key]) * 100).toFixed(1);

    return (
      <div data-testid="timeline-tab" style={{ paddingBottom: 48 }}>
        {/* Intro */}
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 22, fontWeight: 900, fontFamily: "Outfit, sans-serif", color: text, marginBottom: 6 }}>
            Political Funding Over Time
          </h2>
          <p style={{ fontSize: 13, color: muted, maxWidth: 640, lineHeight: 1.6 }}>
            How declared income to Australian political parties has changed year-on-year.
            Spikes in election years (2022–23) are typical as parties ramp up fundraising.
            Data: AEC Transparency Register, declared income only.
          </p>
        </div>

        {/* Delta summary cards */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 28 }}>
          {PARTY_LINES.map((p) => {
            const d = parseFloat(delta(p.key));
            const isUp = d >= 0;
            return (
              <div
                key={p.key}
                style={{
                  background: surface2,
                  border: `1px solid ${bdr2}`,
                  borderRadius: 12,
                  padding: "12px 16px",
                  minWidth: 130,
                  flex: "1 1 130px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 99, background: p.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: text }}>{p.key}</span>
                </div>
                <div style={{ fontSize: 18, fontWeight: 900, fontFamily: "Outfit, sans-serif", color: p.color }}>
                  ${last[p.key]}M
                </div>
                <div style={{ fontSize: 10, color: isUp ? "#10B981" : "#F43F5E", fontWeight: 600 }}>
                  {isUp ? "▲" : "▼"} {Math.abs(d)}% vs FY20-21
                </div>
                <div style={{ fontSize: 10, color: muted }}>FY 2023–24</div>
              </div>
            );
          })}
        </div>

        {/* Main line chart */}
        <div
          style={{
            background: surface2,
            border: `1px solid ${bdr2}`,
            borderRadius: 16,
            padding: "20px",
            marginBottom: 20,
            boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 16px rgba(15,23,42,0.06)",
          }}
          data-testid="timeline-line-chart"
        >
          <p style={{ fontSize: 11, fontWeight: 700, color: muted, marginBottom: 16, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Declared Income Trend — AUD Millions
          </p>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={TIMELINE_DATA} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(51,65,85,0.3)" : "#E2E8F0"} />
              <XAxis dataKey="year" tick={{ fontSize: 12, fill: muted }} axisLine={false} tickLine={false} />
              <YAxis
                tickFormatter={(v) => `$${v}M`}
                tick={{ fontSize: 11, fill: muted }}
                axisLine={false}
                tickLine={false}
                domain={[0, 120]}
              />
              <Tooltip
                formatter={(v, name) => [`$${v}M`, name]}
                contentStyle={{
                  background: isDark ? "rgba(30,41,59,0.97)" : "#fff",
                  border: `1px solid ${bdr2}`,
                  borderRadius: 12,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                  color: text,
                }}
              />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 11, color: muted, paddingTop: 12 }}
              />
              {PARTY_LINES.map((p) => (
                <Line
                  key={p.key}
                  type="monotone"
                  dataKey={p.key}
                  stroke={p.color}
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: p.color, stroke: isDark ? "#1E293B" : "#fff", strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                  name={p.name}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Bar chart — per-year stacked comparison */}
        <div
          style={{
            background: surface2,
            border: `1px solid ${bdr2}`,
            borderRadius: 16,
            padding: "20px",
            marginBottom: 20,
            boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 16px rgba(15,23,42,0.06)",
          }}
          data-testid="timeline-bar-chart"
        >
          <p style={{ fontSize: 11, fontWeight: 700, color: muted, marginBottom: 16, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Grouped by Year — Primary Parties
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={TIMELINE_DATA} barGap={3} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(51,65,85,0.3)" : "#E2E8F0"} />
              <XAxis dataKey="year" tick={{ fontSize: 12, fill: muted }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => `$${v}M`} tick={{ fontSize: 11, fill: muted }} axisLine={false} tickLine={false} domain={[0, 120]} />
              <Tooltip
                formatter={(v, name) => [`$${v}M`, name]}
                contentStyle={{
                  background: isDark ? "rgba(30,41,59,0.97)" : "#fff",
                  border: `1px solid ${bdr2}`,
                  borderRadius: 12,
                  color: text,
                }}
              />
              {PARTY_LINES.slice(0, 4).map((p) => (
                <Bar key={p.key} dataKey={p.key} fill={p.color} name={p.name} radius={[3, 3, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Key insight callout */}
        <div
          style={{
            background: isDark ? "rgba(234,179,8,0.06)" : "rgba(234,179,8,0.06)",
            border: "1px solid rgba(234,179,8,0.18)",
            borderRadius: 14,
            padding: "16px 20px",
          }}
        >
          <p style={{ fontSize: 12, fontWeight: 700, color: isDark ? "#FACC15" : "#92400E", marginBottom: 6 }}>
            Key Insights from 4 Years of AEC Data
          </p>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {[
              "ALP income spiked 41% in 2022–23 (federal election year) — typical for governing parties.",
              "Liberal Party income fell 32% after the 2022 election loss, then partially recovered by 2023–24.",
              "The Greens have grown funding by 20% since 2020–21, reflecting rising vote share.",
              "UAP's $7.4M spike in 2021–22 coincides with Clive Palmer's record $117M election spend.",
              "All figures are declared income above the AEC disclosure threshold (~$16,900/year).",
            ].map((insight, i) => (
              <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                <span style={{ color: "#EAB308", fontSize: 12, flexShrink: 0, marginTop: 1 }}>•</span>
                <span style={{ fontSize: 12, color: muted, lineHeight: 1.5 }}>{insight}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const TABS = [
    { id: "party",       label: "Party Deep Dive",    icon: <BarChart2 style={{ width: 14, height: 14 }} /> },
    { id: "donor",       label: "Follow The Money",   icon: <DollarSign style={{ width: 14, height: 14 }} /> },
    { id: "leaderboard", label: "Leaderboard",        icon: <Trophy style={{ width: 14, height: 14 }} /> },
    { id: "timeline",    label: "Funding Timeline",   icon: <TrendingUp style={{ width: 14, height: 14 }} /> },
  ];

  return (
    <div style={{ background: bg, minHeight: "100vh", paddingBottom: 80 }} data-testid="donations-page">
      {/* Page header */}
      <div style={{ background: "#0F172A", borderBottom: "4px solid #FFC82E", padding: "36px 24px 32px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <div style={{
              fontSize: 9, fontWeight: 800, padding: "3px 10px",
              border: "2px solid #FFC82E", color: "#FFC82E", letterSpacing: "0.1em",
            }}>AEC VERIFIED DATA · FY 2022-23 & 2023-24</div>
          </div>
          <h1 style={{
            fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 900, lineHeight: 1.05,
            fontFamily: "Outfit, sans-serif", color: "#fff", margin: "0 0 8px",
          }}>
            OzPol <span style={{ color: "#FFC82E" }}>Transparent</span>
          </h1>
          <p style={{ fontSize: 14, color: "#94A3B8", margin: "0 0 16px", maxWidth: 600, lineHeight: 1.6 }}>
            Australian political campaign finance, exposed. Explore who funds political parties,
            trace individual donors, and understand the relationship between money and policy.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {[
              { label: "6 PARTIES", sub: "Federal" },
              { label: "28 DONORS", sub: "Seeded" },
              { label: "55+ RECORDS", sub: "AEC Declared" },
              { label: "2 YEARS", sub: "2022-23 & 2023-24" },
            ].map(({ label, sub }) => (
              <div key={label} style={{
                padding: "8px 14px", border: "2px solid #334155",
                background: "#1E293B",
              }}>
                <div style={{ fontSize: 14, fontWeight: 900, fontFamily: "Outfit, sans-serif", color: "#fff" }}>{label}</div>
                <div style={{ fontSize: 9, color: "#64748B", fontWeight: 600 }}>{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div style={{
        background: isDark ? "#1E293B" : "#fff",
        borderBottom: `2px solid ${bdr}`,
        position: "sticky", top: 60, zIndex: 40,
      }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 24px", display: "flex", gap: 0, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          {TABS.map(t => (
            <button key={t.id} data-testid={`tab-btn-${t.id}`} onClick={() => setTab(t.id)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "14px 20px", fontSize: 12, fontWeight: 800,
                fontFamily: "Outfit, sans-serif", cursor: "pointer",
                border: "none", borderBottom: tab === t.id ? "4px solid #FFC82E" : "4px solid transparent",
                background: "transparent",
                color: tab === t.id ? (isDark ? "#F8FAFC" : "#0F172A") : muted,
                transition: "all 0.15s",
                whiteSpace: "nowrap", flexShrink: 0,
              }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "24px 24px 0" }}>
        {tab === "party"       && renderPartyTab()}
        {tab === "donor"       && renderDonorTab()}
        {tab === "leaderboard" && renderLeaderboard()}
        {tab === "timeline"    && renderTimeline()}
      </div>
    </div>
  );
}
